import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getAttendanceStatus, getAttendanceLogs, getAttendanceReport, deleteAttendanceLog, updateAttendanceLog, getWorkers, createWorker, updateWorker, deleteWorker } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Clock, Users, User, Copy, QrCode, ChevronLeft, ChevronRight, FileSpreadsheet, Trash2, Edit3, Plus, X, Calendar, Check } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import { safeCopy } from '../utils/clipboard';

export default function HR() {
    const { company } = useAuth();
    const [tab, setTab] = useState('live');
    const clockUrl = `${window.location.origin}/clock/${company?.slug || ''}`;

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="hr-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="page-title">HR</h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => { safeCopy(clockUrl, 'Link kopyalandi'); }} className="btn btn-secondary text-sm" data-testid="copy-link-btn"><Copy size={14} /> Link</button>
                        <button onClick={() => printQR(clockUrl, company?.name)} className="btn btn-secondary text-sm" data-testid="print-qr-btn"><QrCode size={14} /> QR Yazdir</button>
                        <a href={clockUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary text-sm" data-testid="open-terminal-btn"><Clock size={14} /> Terminal</a>
                    </div>
                </div>

                {/* Horizontal Tabs */}
                <div className="flex gap-1 border-b border-gray-100 pb-px">
                    {[
                        { k: 'live', l: 'Canli Durum' },
                        { k: 'logs', l: 'Kayitlar' },
                        { k: 'report', l: 'Rapor' },
                        { k: 'workers', l: 'Calisanlar' },
                    ].map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`} data-testid={`tab-${t.k}`}>
                            {t.l}
                        </button>
                    ))}
                </div>

                {tab === 'live' && <LiveStatusTab clockUrl={clockUrl} />}
                {tab === 'logs' && <LogsTab />}
                {tab === 'report' && <ReportTab />}
                {tab === 'workers' && <WorkersTab />}
            </div>
        </DashboardLayout>
    );
}

// ===== QR Print =====
function printQR(url, companyName) {
    const w = window.open('', '_blank', 'width=500,height=600');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:sans-serif;text-align:center;padding:40px}h2{margin:0 0 8px;font-size:20px}p{color:#666;font-size:13px;margin:4px 0 30px}svg{width:280px;height:280px}.url{font-size:11px;color:#999;margin-top:20px;word-break:break-all}</style></head><body><h2>${companyName || 'Flowbil'}</h2><p>Giris/Cikis Terminali</p><div id="qr"></div><p class="url">${url}</p><script src="https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js"><\/script><script>var q=qrcode(0,'M');q.addData('${url}');q.make();document.getElementById('qr').innerHTML=q.createSvgTag({cellSize:8,margin:0});setTimeout(()=>print(),500)<\/script></body></html>`);
    w.document.close();
}

// ===== LIVE STATUS TAB =====
function LiveStatusTab() {
    const [data, setData] = useState({ employees: [], date: '', total_day_minutes: 0, total_day_earnings: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState(new Date());

    useEffect(() => { loadData(); const i = setInterval(loadData, 30000); return () => clearInterval(i); }, [selectedDate]);
    useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

    const loadData = async () => {
        try { const res = await getAttendanceStatus(selectedDate); setData(res.data); }
        catch {}
        finally { setLoading(false); }
    };

    const brusselsTime = time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Brussels' });

    const changeDate = (d) => {
        const dt = new Date(selectedDate);
        dt.setDate(dt.getDate() + d);
        setSelectedDate(dt.toISOString().split('T')[0]);
    };

    const isToday = selectedDate === new Date().toISOString().split('T')[0];
    const dateLabel = new Date(selectedDate + 'T00:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' });
    const activeCount = data.employees.filter(e => e.is_clocked_in).length;

    const calcElapsed = (clockInIso) => {
        if (!clockInIso) return null;
        const diff = Math.max(0, (time.getTime() - new Date(clockInIso).getTime()) / 1000);
        return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
    };

    const calcLiveEarnings = (emp) => {
        let totalMinutes = emp.day_minutes || 0;
        if (emp.is_clocked_in && emp.clock_in_time) {
            const elapsed = Math.max(0, (time.getTime() - new Date(emp.clock_in_time).getTime()) / 60000);
            totalMinutes += elapsed;
        }
        const rate = emp.hourly_rate || 0;
        return (totalMinutes / 60) * rate;
    };

    const calcTotalEarnings = () => {
        return data.employees.reduce((sum, emp) => sum + calcLiveEarnings(emp), 0);
    };

    const formatMins = (m) => `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;

    return (
        <div className="space-y-4">
            {/* Date nav + clock */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1 min-w-0">
                    <button onClick={() => changeDate(-1)} className="btn-icon flex-shrink-0" data-testid="prev-date"><ChevronLeft size={18} /></button>
                    <div className="flex items-center gap-1 min-w-0">
                        <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="form-input w-[130px] text-sm py-1.5" data-testid="date-picker" />
                        <span className="text-sm text-gray-500 hidden sm:inline">{dateLabel}</span>
                    </div>
                    <button onClick={() => changeDate(1)} className="btn-icon flex-shrink-0" data-testid="next-date"><ChevronRight size={18} /></button>
                    {!isToday && <button onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} className="text-xs text-blue-500 hover:underline ml-1">Bugun</button>}
                </div>
                <div className="text-right flex-shrink-0">
                    <div className="text-xl sm:text-2xl font-light text-gray-900 tabular-nums">{brusselsTime}</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider hidden sm:block">Europe/Brussels</div>
                </div>
            </div>

            {/* Day summary */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-sm">
                <span className="text-gray-500">{data.employees.length} calisan</span>
                <span className="text-green-600 font-medium">{activeCount} aktif</span>
                <span className="text-gray-500">{formatMins(data.total_day_minutes)}</span>
                <span className="font-medium text-gray-900">{'\u20AC'}{calcTotalEarnings().toFixed(2)}</span>
            </div>

            {/* Employee cards */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[1,2,3,4].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
            ) : data.employees.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="employee-grid">
                    {data.employees.map(s => (
                        <div key={s.employee_id} className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${s.is_clocked_in ? 'bg-green-50/50 border-green-100' : 'bg-gray-50/50 border-gray-100'}`} data-testid={`employee-card-${s.employee_id}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${s.is_clocked_in ? 'bg-green-100' : 'bg-gray-200'}`}>
                                    <User size={18} className={s.is_clocked_in ? 'text-green-600' : 'text-gray-500'} />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-900">{s.employee_name}</div>
                                    <div className="text-xs text-gray-400">
                                        {s.is_clocked_in
                                            ? <span className="text-green-600">{calcElapsed(s.clock_in_time)} · {'\u20AC'}{calcLiveEarnings(s).toFixed(2)}</span>
                                            : s.day_minutes > 0
                                                ? <span>{formatMins(s.day_minutes)} · {'\u20AC'}{s.day_earnings.toFixed(2)}</span>
                                                : 'Giris yapmadi'
                                        }
                                        {s.day_entries > 1 && <span className="text-blue-500 ml-1">({s.day_entries}x)</span>}
                                    </div>
                                </div>
                            </div>
                            {s.is_clocked_in && <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-lg">IN</span>}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 text-gray-400 text-sm">Calisan bulunamadi</div>
            )}
        </div>
    );
}

// ===== LOGS TAB =====
function LogsTab() {
    const LOG_PAGE_SIZE = 20;
    const [logs, setLogs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employeeFilter, setEmployeeFilter] = useState('');
    const [periodFilter, setPeriodFilter] = useState('month');
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editClockIn, setEditClockIn] = useState('');
    const [editClockOut, setEditClockOut] = useState('');
    const [logPage, setLogPage] = useState(1);

    const MONTHS = [
        { value: '01', label: 'Ocak' }, { value: '02', label: 'Subat' },
        { value: '03', label: 'Mart' }, { value: '04', label: 'Nisan' },
        { value: '05', label: 'Mayis' }, { value: '06', label: 'Haziran' },
        { value: '07', label: 'Temmuz' }, { value: '08', label: 'Agustos' },
        { value: '09', label: 'Eylul' }, { value: '10', label: 'Ekim' },
        { value: '11', label: 'Kasim' }, { value: '12', label: 'Aralik' },
    ];

    const currentMonth = selectedMonth;

    useEffect(() => { loadData(); }, [employeeFilter, periodFilter, customStart, customEnd, selectedMonth]);

    const getDateRange = () => {
        const today = new Date();
        if (periodFilter === 'custom' && customStart && customEnd) {
            return { start_date: customStart, end_date: customEnd };
        }
        if (periodFilter === 'week') {
            const day = today.getDay();
            const diff = day === 0 ? 6 : day - 1;
            const start = new Date(today); start.setDate(today.getDate() - diff);
            return { start_date: start.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0] };
        }
        // month filter - use selectedMonth
        const [year, month] = selectedMonth.split('-').map(Number);
        const start = new Date(year, month - 1, 1);
        const end = new Date(year, month, 0); // last day of month
        const todayStr = today.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];
        return { start_date: start.toISOString().split('T')[0], end_date: endStr > todayStr ? todayStr : endStr };
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const range = getDateRange();
            const params = { ...range };
            if (employeeFilter) params.employee_id = employeeFilter;
            const [logsRes, workersRes, reportRes] = await Promise.all([
                getAttendanceLogs(params),
                getWorkers(),
                getAttendanceReport(currentMonth, employeeFilter || undefined),
            ]);
            setLogs(logsRes.data);
            setEmployees(workersRes.data);
            setReport(reportRes.data);
        } catch { toast.error('Veriler yuklenemedi'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu kaydi silmek istediginize emin misiniz?')) return;
        try { await deleteAttendanceLog(id); toast.success('Silindi'); loadData(); }
        catch { toast.error('Silinemedi'); }
    };

    const startEdit = (log) => {
        setEditingId(log.id);
        const toTimeInput = (iso) => {
            if (!iso) return '';
            const d = new Date(iso);
            const h = String(d.getUTCHours()).padStart(2, '0');
            const m = String(d.getUTCMinutes()).padStart(2, '0');
            return `${h}:${m}`;
        };
        setEditClockIn(toTimeInput(log.clock_in));
        setEditClockOut(toTimeInput(log.clock_out));
    };

    const cancelEdit = () => { setEditingId(null); setEditClockIn(''); setEditClockOut(''); };

    const handleEditSave = async (log) => {
        try {
            const dateStr = log.date;
            const update = {};
            if (editClockIn) update.clock_in = `${dateStr}T${editClockIn}:00`;
            if (editClockOut) update.clock_out = `${dateStr}T${editClockOut}:00`;
            if (!update.clock_in && !update.clock_out) return cancelEdit();
            await updateAttendanceLog(log.id, update);
            toast.success('Kayit guncellendi');
            cancelEdit();
            loadData();
        } catch { toast.error('Guncellenemedi'); }
    };

    const formatTime = (iso) => iso ? new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' }) : '--:--';
    const formatDuration = (m) => m ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : '--';
    const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });

    const exportExcel = () => {
        if (!report?.employees?.length) return;
        const rows = [];
        for (const emp of report.employees) {
            for (const day of emp.days) {
                for (const entry of day.entries) {
                    rows.push({ 'Calisan': emp.employee_name, 'Tarih': day.date, 'Giris': formatTime(entry.clock_in), 'Cikis': entry.clock_out ? formatTime(entry.clock_out) : '', 'Sure (dk)': Math.round(entry.duration_minutes || 0) });
                }
            }
            rows.push({ 'Calisan': emp.employee_name, 'Tarih': 'TOPLAM', 'Cikis': `${emp.total_hours}h`, 'Sure (dk)': `${emp.total_payment} EUR` });
            rows.push({});
        }
        rows.push({ 'Calisan': 'GENEL TOPLAM', 'Cikis': `${report.grand_total_hours}h`, 'Sure (dk)': `${report.grand_total_payment} EUR` });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Yoklama');
        XLSX.writeFile(wb, `yoklama_${currentMonth}.xlsx`);
        toast.success('Excel indirildi');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <select value={periodFilter} onChange={e => { setPeriodFilter(e.target.value); setLogPage(1); }} className="form-select w-auto text-sm" data-testid="period-filter">
                        <option value="month">Ay</option>
                        <option value="week">Bu Hafta</option>
                        <option value="custom">Ozel Tarih</option>
                    </select>
                    {periodFilter === 'month' && (
                        <div className="flex items-center gap-1">
                            <select value={selectedMonth.split('-')[1]} onChange={e => {
                                const year = selectedMonth.split('-')[0];
                                setSelectedMonth(`${year}-${e.target.value}`);
                                setLogPage(1);
                            }} className="form-select w-auto text-sm" data-testid="month-select">
                                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                            <select value={selectedMonth.split('-')[0]} onChange={e => {
                                const month = selectedMonth.split('-')[1];
                                setSelectedMonth(`${e.target.value}-${month}`);
                                setLogPage(1);
                            }} className="form-select w-auto text-sm" data-testid="year-select">
                                {[...Array(3)].map((_, i) => {
                                    const y = new Date().getFullYear() - i;
                                    return <option key={y} value={y}>{y}</option>;
                                })}
                            </select>
                        </div>
                    )}
                    {periodFilter === 'custom' && (
                        <div className="flex items-center gap-1">
                            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="form-input w-auto text-sm py-1.5" data-testid="custom-start" />
                            <span className="text-gray-300">-</span>
                            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="form-input w-auto text-sm py-1.5" data-testid="custom-end" />
                        </div>
                    )}
                    <select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)} className="form-select w-auto text-sm" data-testid="employee-filter">
                        <option value="">Tum Calisanlar</option>
                        {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                    </select>
                </div>
                <button onClick={exportExcel} disabled={!report?.employees?.length} className="btn btn-secondary text-sm" data-testid="export-excel-btn"><FileSpreadsheet size={14} /> Excel</button>
            </div>

            {/* Summary cards */}
            {report?.employees?.length > 0 && (
                <div className="flex gap-3 overflow-x-auto pb-1" data-testid="summary-cards">
                    {report.employees.map(emp => (
                        <div key={emp.employee_id} className="flex-shrink-0 min-w-[170px] p-3 rounded-xl bg-gray-50 border border-gray-100">
                            <div className="text-sm font-medium text-gray-900">{emp.employee_name}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{emp.work_days} gun · {emp.total_hours}h</div>
                            <div className="text-base font-semibold text-gray-900 mt-0.5">{'\u20AC'}{emp.total_payment.toFixed(2)}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Log entries with pagination */}
            {loading ? (
                <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="skeleton h-11 rounded-lg" />)}</div>
            ) : logs.length > 0 ? (
                <>
                <div className="space-y-0.5" data-testid="log-list">
                    {logs.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE).map(log => (
                        <div key={log.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-gray-50 transition-colors group" data-testid={`log-${log.id}`}>
                            <div className="flex items-center gap-4 flex-1">
                                <span className="text-xs text-gray-400 min-w-[44px]">{formatDate(log.date)}</span>
                                <span className="text-sm font-medium text-gray-900 min-w-[100px]">{log.employee_name}</span>
                                {editingId === log.id ? (
                                    <div className="flex items-center gap-1.5">
                                        <input type="time" value={editClockIn} onChange={e => setEditClockIn(e.target.value)} className="form-input w-[90px] text-sm py-0.5 px-1.5" data-testid={`edit-clock-in-${log.id}`} />
                                        <span className="text-gray-300">{'\u2192'}</span>
                                        <input type="time" value={editClockOut} onChange={e => setEditClockOut(e.target.value)} className="form-input w-[90px] text-sm py-0.5 px-1.5" data-testid={`edit-clock-out-${log.id}`} />
                                    </div>
                                ) : (
                                    <span className="text-sm text-gray-600">
                                        {formatTime(log.clock_in)} <span className="text-gray-300 mx-0.5">{'\u2192'}</span> {log.clock_out ? formatTime(log.clock_out) : <span className="text-green-500 text-xs font-medium">aktif</span>}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {editingId !== log.id && log.duration_minutes > 0 && <span className="text-sm font-medium text-gray-700">{formatDuration(log.duration_minutes)}</span>}
                                {log.is_late && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                                {editingId === log.id ? (
                                    <>
                                        <button onClick={() => handleEditSave(log)} className="btn-icon text-green-600 hover:text-green-700" data-testid={`save-edit-${log.id}`}><Check size={15} /></button>
                                        <button onClick={cancelEdit} className="btn-icon text-gray-400 hover:text-gray-600" data-testid={`cancel-edit-${log.id}`}><X size={15} /></button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => startEdit(log)} className="btn-icon opacity-0 group-hover:opacity-100 hover:text-blue-500" data-testid={`edit-${log.id}`}><Edit3 size={13} /></button>
                                        <button onClick={() => handleDelete(log.id)} className="btn-icon opacity-0 group-hover:opacity-100 hover:text-red-500" data-testid={`del-${log.id}`}><Trash2 size={13} /></button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {logs.length > LOG_PAGE_SIZE && (
                    <div className="flex items-center justify-between pt-3 text-sm" data-testid="log-pagination">
                        <span className="text-gray-400">{logs.length} kayit</span>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setLogPage(p => Math.max(1, p - 1))} disabled={logPage === 1} className="btn-icon disabled:opacity-30" data-testid="log-prev"><ChevronLeft size={16} /></button>
                            <span className="text-gray-600 min-w-[60px] text-center">{logPage} / {Math.ceil(logs.length / LOG_PAGE_SIZE)}</span>
                            <button onClick={() => setLogPage(p => Math.min(Math.ceil(logs.length / LOG_PAGE_SIZE), p + 1))} disabled={logPage >= Math.ceil(logs.length / LOG_PAGE_SIZE)} className="btn-icon disabled:opacity-30" data-testid="log-next"><ChevronRight size={16} /></button>
                        </div>
                    </div>
                )}
                </>
            ) : (
                <div className="text-center py-16 text-gray-400 text-sm">Bu donem icin kayit yok</div>
            )}
        </div>
    );
}


// ===== REPORT TAB =====
function ReportTab() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [dailyHours, setDailyHours] = useState(8);
    const [workDays, setWorkDays] = useState([1, 2, 3, 4, 5]); // Mon-Fri

    const MONTHS = [
        { v: '01', l: 'Ocak' }, { v: '02', l: 'Subat' }, { v: '03', l: 'Mart' },
        { v: '04', l: 'Nisan' }, { v: '05', l: 'Mayis' }, { v: '06', l: 'Haziran' },
        { v: '07', l: 'Temmuz' }, { v: '08', l: 'Agustos' }, { v: '09', l: 'Eylul' },
        { v: '10', l: 'Ekim' }, { v: '11', l: 'Kasim' }, { v: '12', l: 'Aralik' },
    ];
    const DAY_NAMES = ['Paz', 'Pzt', 'Sal', 'Car', 'Per', 'Cum', 'Cmt'];
    const DAY_OPTIONS = [
        { v: 1, l: 'Pzt' }, { v: 2, l: 'Sal' }, { v: 3, l: 'Car' },
        { v: 4, l: 'Per' }, { v: 5, l: 'Cum' }, { v: 6, l: 'Cmt' }, { v: 0, l: 'Paz' },
    ];

    useEffect(() => { loadReport(); }, [selectedMonth]);

    const loadReport = async () => {
        setLoading(true);
        try {
            const res = await getAttendanceReport(selectedMonth);
            setReport(res.data);
        } catch { toast.error('Rapor yuklenemedi'); }
        finally { setLoading(false); }
    };

    const toggleDay = (d) => setWorkDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

    // Build calendar days for the month
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const calendarDays = useMemo(() => {
        const [year, month] = selectedMonth.split('-').map(Number);
        const daysInMonth = new Date(year, month, 0).getDate();
        const days = [];
        for (let i = 1; i <= daysInMonth; i++) {
            const date = new Date(year, month - 1, i);
            const dayOfWeek = date.getDay();
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            days.push({ day: i, dayOfWeek, dateStr, isWorkDay: workDays.includes(dayOfWeek), isPast: dateStr <= todayStr });
        }
        return days;
    }, [selectedMonth, workDays, todayStr]);

    // Build employee rows
    const rows = useMemo(() => {
        if (!report?.employees) return [];
        return report.employees.filter(e => e.employee_name).map(emp => {
            const dayMap = {};
            (emp.days || []).forEach(d => { dayMap[d.date] = Math.round(d.total_minutes) / 60; });
            let totalWorked = 0;
            let totalExpected = 0;
            let totalDeficit = 0;
            const cells = calendarDays.map(cd => {
                const hours = dayMap[cd.dateStr] || 0;
                const expected = (cd.isWorkDay && cd.isPast) ? dailyHours : 0;
                const deficit = (cd.isWorkDay && cd.isPast) ? Math.max(0, expected - hours) : 0;
                if (cd.isWorkDay && cd.isPast) {
                    totalExpected += expected;
                    totalWorked += hours;
                    totalDeficit += deficit;
                }
                return { ...cd, hours: Math.round(hours * 10) / 10, expected, deficit: Math.round(deficit * 10) / 10 };
            });
            return { name: emp.employee_name, id: emp.employee_id, cells, totalWorked: Math.round(totalWorked * 10) / 10, totalExpected, totalDeficit: Math.round(totalDeficit * 10) / 10 };
        });
    }, [report, calendarDays, dailyHours]);

    const exportExcel = () => {
        if (!rows.length) return;
        const headerRow = ['Calisan', ...calendarDays.map(d => `${d.day} ${DAY_NAMES[d.dayOfWeek]}`), 'Toplam', 'Beklenen', 'Eksik'];
        const dataRows = rows.map(r => [
            r.name,
            ...r.cells.map(c => c.isWorkDay ? (c.hours > 0 ? `${c.hours}h` : 'YOK') : '-'),
            `${r.totalWorked}h`, `${r.totalExpected}h`, `${r.totalDeficit}h`
        ]);
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
        // Column widths
        ws['!cols'] = [{ wch: 20 }, ...calendarDays.map(() => ({ wch: 7 })), { wch: 10 }, { wch: 10 }, { wch: 10 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Devamsizlik');
        const [y, m] = selectedMonth.split('-');
        XLSX.writeFile(wb, `devamsizlik_${MONTHS[parseInt(m) - 1].l}_${y}.xlsx`);
        toast.success('Excel indirildi');
    };

    return (
        <div className="space-y-4">
            {/* Controls */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1">
                    <select value={selectedMonth.split('-')[1]} onChange={e => setSelectedMonth(`${selectedMonth.split('-')[0]}-${e.target.value}`)} className="form-select w-auto text-sm" data-testid="report-month">
                        {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <select value={selectedMonth.split('-')[0]} onChange={e => setSelectedMonth(`${e.target.value}-${selectedMonth.split('-')[1]}`)} className="form-select w-auto text-sm" data-testid="report-year">
                        {[...Array(3)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
                    </select>
                </div>
                <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-500">Gunluk saat:</label>
                    <input type="number" value={dailyHours} onChange={e => setDailyHours(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))} onBlur={e => { if (e.target.value === '' || Number(e.target.value) < 1) setDailyHours(8); }} className="form-input w-16 text-sm py-1.5 text-center" min="1" max="24" data-testid="daily-hours" />
                </div>
                <div className="flex items-center gap-1">
                    <label className="text-xs text-gray-500 mr-1">Calisma gunleri:</label>
                    {DAY_OPTIONS.map(d => (
                        <button key={d.v} onClick={() => toggleDay(d.v)} className={`px-2 py-1 text-xs rounded font-medium transition-colors ${workDays.includes(d.v) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-testid={`wd-${d.l}`}>{d.l}</button>
                    ))}
                </div>
                <button onClick={exportExcel} disabled={!rows.length} className="btn btn-secondary text-sm ml-auto" data-testid="report-excel"><FileSpreadsheet size={14} /> Excel</button>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="skeleton h-10 rounded-lg" />)}</div>
            ) : rows.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                    <table className="w-full text-xs" data-testid="report-table">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left px-3 py-2 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[140px]">Calisan</th>
                                {calendarDays.map(d => (
                                    <th key={d.day} className={`px-1 py-2 text-center font-medium min-w-[40px] ${!d.isWorkDay ? 'text-gray-300 bg-gray-50' : 'text-gray-600'}`}>
                                        <div>{d.day}</div>
                                        <div className="text-[10px] font-normal">{DAY_NAMES[d.dayOfWeek]}</div>
                                    </th>
                                ))}
                                <th className="px-2 py-2 text-center font-semibold text-gray-700 min-w-[55px]">Toplam</th>
                                <th className="px-2 py-2 text-center font-semibold text-gray-700 min-w-[55px]">Beklenen</th>
                                <th className="px-2 py-2 text-center font-semibold text-red-600 min-w-[55px]">Eksik</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                                    <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white z-10">{r.name}</td>
                                    {r.cells.map((c, i) => (
                                        <td key={i} className={`px-1 py-2 text-center ${!c.isWorkDay ? 'bg-gray-50 text-gray-300' : !c.isPast ? 'bg-gray-50/50 text-gray-300' : c.hours === 0 ? 'bg-red-50 text-red-600 font-semibold' : c.deficit > 0 ? 'bg-orange-50 text-orange-600' : 'text-green-600'}`}>
                                            {!c.isWorkDay ? '-' : !c.isPast ? '' : c.hours > 0 ? `${c.hours}` : 'YOK'}
                                        </td>
                                    ))}
                                    <td className="px-2 py-2 text-center font-semibold text-gray-900">{r.totalWorked}h</td>
                                    <td className="px-2 py-2 text-center font-medium text-gray-500">{r.totalExpected}h</td>
                                    <td className={`px-2 py-2 text-center font-semibold ${r.totalDeficit > 0 ? 'text-red-600' : 'text-green-600'}`}>{r.totalDeficit > 0 ? `-${r.totalDeficit}h` : '0h'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-16 text-gray-400 text-sm">Bu donem icin veri yok</div>
            )}

            {/* Deficit Table - Muhasebe Raporu */}
            {!loading && rows.length > 0 && rows.some(r => r.totalDeficit > 0) && (
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-gray-800">Eksik Saatler (Muhasebe)</h3>
                        <button onClick={() => {
                            const dh = typeof dailyHours === 'number' ? dailyHours : 8;
                            const deficitRows = rows.filter(r => r.totalDeficit > 0);
                            if (!deficitRows.length) return;
                            const pastWorkDays = calendarDays.filter(d => d.isWorkDay && d.isPast);
                            const headerRow = ['Calisan', ...pastWorkDays.map(d => `${d.day} ${DAY_NAMES[d.dayOfWeek]}`), 'Toplam Eksik'];
                            const dataRows = deficitRows.map(r => {
                                const cells = pastWorkDays.map(cd => {
                                    const cell = r.cells.find(c => c.dateStr === cd.dateStr);
                                    if (!cell || cell.deficit <= 0) return '';
                                    return cell.hours === 0 ? `-${dh}` : `-${cell.deficit}`;
                                });
                                return [r.name, ...cells, `-${r.totalDeficit}h`];
                            });
                            const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
                            ws['!cols'] = [{ wch: 20 }, ...pastWorkDays.map(() => ({ wch: 8 })), { wch: 12 }];
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, 'Eksik Saatler');
                            const [y, m] = selectedMonth.split('-');
                            XLSX.writeFile(wb, `eksik_saatler_${MONTHS[parseInt(m) - 1].l}_${y}.xlsx`);
                            toast.success('Eksik saatler Excel indirildi');
                        }} className="btn btn-secondary text-sm" data-testid="deficit-excel"><FileSpreadsheet size={14} /> Excel</button>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-red-200 bg-white">
                        <table className="w-full text-xs" data-testid="deficit-table">
                            <thead>
                                <tr className="bg-red-50">
                                    <th className="text-left px-3 py-2 font-semibold text-gray-700 sticky left-0 bg-red-50 z-10 min-w-[140px]">Calisan</th>
                                    {calendarDays.filter(d => d.isWorkDay && d.isPast).map(d => (
                                        <th key={d.day} className="px-1 py-2 text-center font-medium text-gray-600 min-w-[44px]">
                                            <div>{d.day}</div>
                                            <div className="text-[10px] font-normal">{DAY_NAMES[d.dayOfWeek]}</div>
                                        </th>
                                    ))}
                                    <th className="px-2 py-2 text-center font-semibold text-red-600 min-w-[70px]">Toplam Eksik</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.filter(r => r.totalDeficit > 0).map(r => (
                                    <tr key={r.id} className="border-t border-red-100 hover:bg-red-50/30">
                                        <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white z-10">{r.name}</td>
                                        {calendarDays.filter(d => d.isWorkDay && d.isPast).map(d => {
                                            const cell = r.cells.find(c => c.dateStr === d.dateStr);
                                            const deficit = cell?.deficit || 0;
                                            const dh = typeof dailyHours === 'number' ? dailyHours : 8;
                                            if (deficit <= 0) return <td key={d.day} className="px-1 py-2 text-center text-gray-300">-</td>;
                                            return <td key={d.day} className="px-1 py-2 text-center font-semibold text-red-600">{cell.hours === 0 ? `-${dh}` : `-${deficit}`}</td>;
                                        })}
                                        <td className="px-2 py-2 text-center font-bold text-red-600">-{r.totalDeficit}h</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}


// ===== WORKERS TAB =====
function WorkersTab() {
    const [workers, setWorkers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [quickMode, setQuickMode] = useState(false);
    const [form, setForm] = useState({ full_name: '', pin: '', hourly_rate: '', monthly_hours: '', work_start_time: '', work_end_time: '' });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try { setWorkers((await getWorkers()).data); }
        catch { toast.error('Yuklenemedi'); }
        finally { setLoading(false); }
    };

    const openAdd = (quick = false) => {
        setEditing(null); setQuickMode(quick);
        setForm({ full_name: '', pin: '', hourly_rate: '', monthly_hours: '', work_start_time: '', work_end_time: '' });
        setShowModal(true);
    };

    const openEdit = (w) => {
        setEditing(w); setQuickMode(false);
        setForm({ full_name: w.full_name, pin: w.pin || '', hourly_rate: w.hourly_rate || '', monthly_hours: w.monthly_hours || '', work_start_time: w.work_start_time || '', work_end_time: w.work_end_time || '' });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = { full_name: form.full_name, pin: form.pin || null, hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null, monthly_hours: form.monthly_hours ? parseFloat(form.monthly_hours) : null, work_start_time: form.work_start_time || null, work_end_time: form.work_end_time || null };
        try {
            if (editing) { await updateWorker(editing.id, data); toast.success('Guncellendi'); }
            else { await createWorker(data); toast.success('Eklendi'); }
            setShowModal(false); loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Silmek istediginize emin misiniz?')) return;
        try { await deleteWorker(id); toast.success('Silindi'); loadData(); }
        catch { toast.error('Silinemedi'); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{workers.length} calisan</span>
                <div className="flex gap-2">
                    <button onClick={() => openAdd(true)} className="btn btn-secondary text-sm" data-testid="quick-add-btn"><Plus size={14} /> Hizli Ekle</button>
                    <button onClick={() => openAdd(false)} className="btn btn-primary text-sm" data-testid="add-worker-btn"><Plus size={14} /> Ekle</button>
                </div>
            </div>

            {loading ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
            ) : workers.length > 0 ? (
                <div className="space-y-0.5" data-testid="workers-list">
                    {workers.map(w => (
                        <div key={w.id} className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors group" data-testid={`worker-${w.id}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center"><User size={16} className="text-gray-500" /></div>
                                <span className="text-sm font-medium text-gray-900">{w.full_name}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="text-right text-xs text-gray-400 hidden md:flex items-center gap-3">
                                    {w.pin && <span className="font-mono">PIN: {w.pin}</span>}
                                    {w.hourly_rate && <span>{'\u20AC'}{w.hourly_rate}/h</span>}
                                    {w.monthly_hours && <span>{w.monthly_hours}h/ay</span>}
                                    {w.work_start_time && w.work_end_time && <span>{w.work_start_time}-{w.work_end_time}</span>}
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => openEdit(w)} className="btn-icon hover:text-blue-600" data-testid={`edit-${w.id}`}><Edit3 size={14} /></button>
                                    <button onClick={() => handleDelete(w.id)} className="btn-icon hover:text-red-500" data-testid={`del-${w.id}`}><Trash2 size={14} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-16">
                    <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-base font-medium text-gray-900 mb-3">Henuz calisan yok</h3>
                    <button onClick={() => openAdd(false)} className="btn btn-primary text-sm"><Plus size={14} /> Calisan Ekle</button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()} role="dialog" aria-label="Calisan formu" data-testid="worker-modal">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h3 className="font-medium text-gray-900">{editing ? 'Duzenle' : quickMode ? 'Hizli Ekle' : 'Calisan Ekle'}</h3>
                            <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={20} className="text-gray-500" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-4 space-y-3">
                            <div>
                                <label className="form-label">Ad Soyad</label>
                                <input type="text" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} className="form-input text-sm" required autoFocus data-testid="name-input" />
                            </div>
                            <div>
                                <label className="form-label">PIN (4 hane)</label>
                                <input type="text" maxLength={4} value={form.pin} onChange={e => setForm(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))} placeholder="1234" className="form-input text-sm" data-testid="pin-input" />
                            </div>
                            {!quickMode && (
                                <>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="form-label">Ucret ({'\u20AC'}/h)</label>
                                            <input type="number" step="0.01" value={form.hourly_rate} onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))} placeholder="15" className="form-input text-sm" data-testid="rate-input" />
                                        </div>
                                        <div>
                                            <label className="form-label">Aylik Saat</label>
                                            <input type="number" value={form.monthly_hours} onChange={e => setForm(p => ({ ...p, monthly_hours: e.target.value }))} placeholder="176" className="form-input text-sm" data-testid="hours-input" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="form-label">Vardiya Baslangic</label>
                                            <input type="time" value={form.work_start_time} onChange={e => setForm(p => ({ ...p, work_start_time: e.target.value }))} className="form-input text-sm" data-testid="start-input" />
                                        </div>
                                        <div>
                                            <label className="form-label">Vardiya Bitis</label>
                                            <input type="time" value={form.work_end_time} onChange={e => setForm(p => ({ ...p, work_end_time: e.target.value }))} className="form-input text-sm" data-testid="end-input" />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="flex gap-3 pt-1">
                                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary flex-1">Iptal</button>
                                <button type="submit" className="btn btn-primary flex-1" data-testid="save-btn">Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
